import { useEffect, useState } from "react";
import api, { API, formatCLP, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Buildings, Users, CheckCircle, DownloadSimple, UserPlus, UploadSimple, Plus, Ticket, ShoppingCart, Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function CompanyPanel() {
  const [dash, setDash] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [newWorker, setNewWorker] = useState({ name: "", email: "", rut: "" });
  const [selWorkers, setSelWorkers] = useState([]);
  const [selCourses, setSelCourses] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [licenses, setLicenses] = useState([]);
  const [pricing, setPricing] = useState({ convenio_discount_pct: 0 });
  const [seats, setSeats] = useState({});
  const [newCreds, setNewCreds] = useState([]);

  const load = () => {
    api.get("/company/dashboard").then((r) => setDash(r.data)).catch(() => {});
    api.get("/company/workers").then((r) => setWorkers(r.data)).catch(() => {});
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
    api.get("/company/licenses").then((r) => setLicenses(r.data)).catch(() => {});
    api.get("/company/pricing").then((r) => setPricing(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const tierPct = (n) => (n >= 50 ? 20 : n >= 25 ? 15 : n >= 10 ? 10 : 0);
  const buySeats = async () => {
    const items = Object.entries(seats).filter(([, v]) => parseInt(v) > 0).map(([course_id, v]) => ({ course_id, seats: parseInt(v) }));
    if (!items.length) { toast.error("Indica la cantidad de cupos a comprar"); return; }
    try { const { data } = await api.post("/company/purchase", { items }); toast.success(`Compra de convenio realizada: ${formatCLP(data.total)}`); setSeats({}); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const addWorker = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/company/workers", newWorker);
      if (data.temp_password) setNewCreds([{ email: data.email, temp_password: data.temp_password }]);
      toast.success("Trabajador agregado"); setNewWorker({ name: "", email: "", rut: "" }); setAddOpen(false); load();
    }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const uploadCsv = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/company/workers/csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.credentials?.length) setNewCreds(data.credentials);
      toast.success(`${data.added} trabajadores cargados`); load();
    } catch { toast.error("Error al cargar CSV"); }
  };

  const assign = async () => {
    if (!selWorkers.length || !selCourses.length) { toast.error("Selecciona trabajadores y cursos"); return; }
    try {
      const { data } = await api.post("/company/assign", { worker_ids: selWorkers, course_ids: selCourses });
      toast.success(`${data.assigned} asignaciones creadas`);
      if (data.skipped_no_seats > 0) toast.warning(`${data.skipped_no_seats} no se asignaron por falta de cupos. Compra más en "Comprar cupos".`);
      setSelWorkers([]); setSelCourses([]); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const stats = [
    { label: "Trabajadores", value: dash?.workers_count ?? 0, icon: Users },
    { label: "Cursos asignados", value: dash?.total_enrollments ?? 0, icon: Buildings },
    { label: "Aprobados", value: dash?.completed ?? 0, icon: CheckCircle },
  ];

  return (
    <div>
      <p className="text-xs uppercase tracking-overline text-primary mb-1 font-mono">Panel de Empresa</p>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <h1 className="font-display text-3xl font-800 tracking-tight">Capacitación de tu equipo</h1>
        <a href={`${API}/company/report.csv`} target="_blank" rel="noreferrer">
          <Button variant="outline" className="gap-2" data-testid="company-report-btn"><DownloadSimple size={18} /> Descargar reporte CSV</Button>
        </a>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        {stats.map((s) => { const Icon = s.icon; return (
          <div key={s.label} className="bg-white border border-border rounded-lg p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-md bg-primary/10 flex items-center justify-center"><Icon size={24} className="text-primary" weight="duotone" /></div>
            <div><div className="font-display text-2xl font-800">{s.value}</div><div className="text-sm text-muted-foreground">{s.label}</div></div>
          </div>
        );})}
      </div>

      <Tabs defaultValue="progress">
        <TabsList>
          <TabsTrigger value="progress" data-testid="tab-progress">Avance del equipo</TabsTrigger>
          <TabsTrigger value="workers" data-testid="tab-workers">Trabajadores</TabsTrigger>
          <TabsTrigger value="assign" data-testid="tab-assign">Asignar cursos</TabsTrigger>
          <TabsTrigger value="buy" data-testid="tab-buy">Comprar cupos</TabsTrigger>
        </TabsList>

        <TabsContent value="progress" className="mt-4">
          <div className="bg-white border border-border rounded-lg overflow-hidden">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Trabajador</TableHead><TableHead>Curso</TableHead>
                <TableHead>Avance</TableHead><TableHead>Nota</TableHead><TableHead>Estado</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(dash?.rows || []).length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Sin asignaciones aún.</TableCell></TableRow>
                ) : dash.rows.map((r, i) => (
                  <TableRow key={i} data-testid={`progress-row-${i}`}>
                    <TableCell className="font-medium">{r.worker_name}</TableCell>
                    <TableCell>{r.course_title}</TableCell>
                    <TableCell className="w-40"><Progress value={r.progress_pct} className="h-2" /><span className="text-xs text-muted-foreground">{r.progress_pct}%</span></TableCell>
                    <TableCell>{r.grade ?? "—"}</TableCell>
                    <TableCell>{r.passed ? <Badge className="bg-primary text-white">Aprobado</Badge> : <Badge variant="outline">En curso</Badge>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="workers" className="mt-4">
          <div className="flex flex-wrap gap-3 mb-4">
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild><Button className="bg-primary text-white hover:bg-blue-600 gap-2" data-testid="add-worker-btn"><UserPlus size={18} /> Agregar trabajador</Button></DialogTrigger>
              <DialogContent className="bg-white">
                <DialogHeader><DialogTitle className="font-display font-800">Nuevo trabajador</DialogTitle></DialogHeader>
                <form onSubmit={addWorker} className="space-y-3">
                  <div><Label>Nombre</Label><Input value={newWorker.name} onChange={(e) => setNewWorker({ ...newWorker, name: e.target.value })} required data-testid="worker-name" className="mt-1" /></div>
                  <div><Label>Correo</Label><Input type="email" value={newWorker.email} onChange={(e) => setNewWorker({ ...newWorker, email: e.target.value })} required data-testid="worker-email" className="mt-1" /></div>
                  <div><Label>RUT</Label><Input value={newWorker.rut} onChange={(e) => setNewWorker({ ...newWorker, rut: e.target.value })} data-testid="worker-rut" className="mt-1" /></div>
                  <DialogFooter><Button type="submit" className="bg-primary text-white hover:bg-blue-600" data-testid="worker-save">Guardar</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            <label>
              <input type="file" accept=".csv" onChange={uploadCsv} className="hidden" data-testid="worker-csv-input" />
              <Button variant="outline" className="gap-2" asChild><span><UploadSimple size={18} /> Cargar CSV</span></Button>
            </label>
          </div>
          <p className="text-xs text-muted-foreground mb-3">Formato CSV: columnas <code className="font-mono">nombre,email,rut</code>. Se genera una contraseña temporal única por trabajador (se muestra una sola vez al crearlo).</p>
          {newCreds.length > 0 && (
            <div className="mb-4 bg-amber-50 border border-amber-300 rounded-lg p-4" data-testid="new-worker-credentials">
              <p className="text-sm font-semibold text-amber-800 mb-2">Contraseñas temporales (cópialas ahora, no se volverán a mostrar):</p>
              <div className="space-y-1">
                {newCreds.map((c) => (
                  <div key={c.email} className="flex items-center gap-3 text-sm font-mono">
                    <span className="text-slate-700">{c.email}</span>
                    <span className="font-bold text-slate-900">{c.temp_password}</span>
                  </div>
                ))}
              </div>
              <button className="mt-3 text-xs text-amber-700 underline" onClick={() => setNewCreds([])} data-testid="dismiss-credentials">Ocultar</button>
            </div>
          )}
          <div className="bg-white border border-border rounded-lg overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Nombre</TableHead><TableHead>Correo</TableHead><TableHead>RUT</TableHead></TableRow></TableHeader>
              <TableBody>
                {workers.length === 0 ? <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-8">No hay trabajadores.</TableCell></TableRow>
                  : workers.map((w) => <TableRow key={w.id}><TableCell className="font-medium">{w.name}</TableCell><TableCell>{w.email}</TableCell><TableCell>{w.rut || "—"}</TableCell></TableRow>)}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="assign" className="mt-4">
          {licenses.some((l) => l.available <= 3) && (
            <div className="mb-3 flex items-center gap-2 bg-accent/10 border border-accent/30 text-amber-700 rounded-md px-3 py-2 text-sm" data-testid="low-seats-warning">
              <Warning size={16} weight="fill" /> Tienes cursos con pocos cupos disponibles. Te sugerimos comprar más en la pestaña <b>"Comprar cupos"</b> antes de asignar.
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white border border-border rounded-lg p-5">
              <h3 className="font-display font-800 mb-3">1. Selecciona trabajadores</h3>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {workers.map((w) => (
                  <label key={w.id} className="flex items-center gap-2 py-1 cursor-pointer">
                    <Checkbox checked={selWorkers.includes(w.id)} onCheckedChange={() => toggle(selWorkers, setSelWorkers, w.id)} data-testid={`assign-worker-${w.id}`} />
                    <span className="text-sm">{w.name} <span className="text-muted-foreground">· {w.email}</span></span>
                  </label>
                ))}
                {workers.length === 0 && <p className="text-sm text-muted-foreground">Agrega trabajadores primero.</p>}
              </div>
            </div>
            <div className="bg-white border border-border rounded-lg p-5">
              <h3 className="font-display font-800 mb-3">2. Selecciona cursos</h3>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {courses.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 py-1 cursor-pointer">
                    <Checkbox checked={selCourses.includes(c.id)} onCheckedChange={() => toggle(selCourses, setSelCourses, c.id)} data-testid={`assign-course-${c.id}`} />
                    <span className="text-sm">{c.title}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <Button onClick={assign} className="mt-5 bg-primary text-white hover:bg-blue-600 gap-2" data-testid="assign-submit"><Plus size={18} /> Asignar cursos ({selWorkers.length}×{selCourses.length})</Button>
        </TabsContent>

        <TabsContent value="buy" className="mt-4">
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white border border-border rounded-lg p-5">
              <h3 className="font-display font-800 mb-1">Compra de cupos masivos</h3>
              <p className="text-sm text-muted-foreground mb-4">Descuento por volumen: 10+ cupos 10%, 25+ 15%, 50+ 20%{pricing.convenio_discount_pct > 0 && <> · convenio empresa +{pricing.convenio_discount_pct}%</>}.</p>
              <div className="space-y-2">
                {courses.map((c) => {
                  const n = parseInt(seats[c.id]) || 0;
                  const pct = Math.min((pricing.convenio_discount_pct || 0) + tierPct(n), 40);
                  const line = c.price * n * (1 - pct / 100);
                  return (
                    <div key={c.id} className="flex items-center gap-3 border border-border rounded-md p-3" data-testid={`buy-row-${c.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm line-clamp-1">{c.title}</p>
                        <p className="text-xs text-muted-foreground">{formatCLP(c.price)} c/u {n >= 10 && <span className="text-primary">· {pct}% dcto</span>}</p>
                      </div>
                      <Input type="number" min="0" value={seats[c.id] || ""} onChange={(e) => setSeats({ ...seats, [c.id]: e.target.value })}
                        placeholder="0" className="w-20" data-testid={`buy-seats-${c.id}`} />
                      <span className="w-28 text-right text-sm font-medium">{n > 0 ? formatCLP(line) : "—"}</span>
                    </div>
                  );
                })}
              </div>
              <Button onClick={buySeats} className="mt-4 bg-primary text-white hover:bg-blue-600 gap-2" data-testid="buy-submit"><ShoppingCart size={18} /> Comprar cupos</Button>
            </div>
            <div className="bg-white border border-border rounded-lg p-5">
              <h3 className="font-display font-800 mb-3 flex items-center gap-2"><Ticket size={18} className="text-primary" /> Cupos disponibles</h3>
              {licenses.length === 0 ? <p className="text-sm text-muted-foreground">Aún no has comprado cupos.</p> : (
                <div className="space-y-3">
                  {licenses.map((l) => (
                    <div key={l.course_id} className="text-sm" data-testid={`license-${l.course_id}`}>
                      <p className="font-medium line-clamp-1">{l.course_title}</p>
                      <p className="text-muted-foreground"><b className={l.available <= 3 ? "text-destructive" : "text-primary"}>{l.available}</b> disponibles de {l.total_seats} ({l.used_seats} asignados){l.available <= 3 && l.available > 0 && <span className="text-amber-600"> · quedan pocos, considera recomprar</span>}{l.available === 0 && <span className="text-destructive"> · sin cupos</span>}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
